import React from "react";
import LottieAnimator from "./ui-elements/LottieAnimator";
import { FiArrowRight, FiPhone } from "react-icons/fi";
import { useLanguage } from "../context/LanguageContext";

export default function HeroBanner({ lottie }) {
  const { t } = useLanguage();

  return (
    <section className="relative w-full pt-28 pb-16 md:pt-36 md:pb-24 bg-gradient-to-b from-slate-50 via-white to-white overflow-hidden">
      {/* Ambient background glow */}
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-red-100/50 rounded-full filter blur-3xl pointer-events-none -z-10" />
      <div className="absolute top-1/3 left-10 w-72 h-72 bg-amber-50 rounded-full filter blur-3xl pointer-events-none -z-10" />

      <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Left Text & CTA */}
          <div className="lg:col-span-7 flex flex-col items-center lg:items-start text-center lg:text-left order-2 lg:order-1">
            {/* Pill Badge */}
            <div className="inline-flex items-center space-x-2 px-3.5 py-1.5 rounded-full bg-red-50 border border-red-100 text-brandRed text-xs font-bold uppercase tracking-wider mb-5">
              <span className="w-2 h-2 rounded-full bg-brandRed animate-ping inline-block" />
              <span>{t("hero.badge")}</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-slate-900 tracking-tight leading-[1.08] uppercase">
              {t("hero.titlePart1")} <span className="text-brandRed">{t("hero.titlePart2")}</span> {t("hero.titlePart3")} <span className="text-brandRed">{t("hero.titlePart4")}</span>
            </h1>

            {/* Subtitle */}
            <p className="mt-5 text-base sm:text-lg text-slate-600 max-w-2xl leading-relaxed">
              {t("hero.subtitle")}
            </p>

            {/* Action Buttons */}
            <div className="mt-8 flex flex-col sm:flex-row items-center gap-3.5 w-full sm:w-auto">
              <button
                onClick={() => {
                  const el = document.getElementById("tariffs");
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                }}
                className="w-full sm:w-auto bg-brandRed hover:bg-brandRedHover text-white text-xs md:text-sm font-bold tracking-wider uppercase px-7 py-4 rounded-full shadow-glow-red hover:shadow-glow-red-lg transform hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 flex items-center justify-center space-x-2 cursor-pointer"
              >
                <span>{t("hero.viewTariffs")}</span>
                <FiArrowRight size={15} />
              </button>

              <a
                href="tel:+37460666999"
                className="w-full sm:w-auto bg-white hover:bg-slate-50 text-slate-800 text-xs md:text-sm font-bold tracking-wider uppercase px-7 py-4 rounded-full border border-slate-200/90 shadow-sm hover:border-slate-300 transition-all duration-200 cursor-pointer flex items-center justify-center space-x-2"
              >
                <FiPhone className="text-brandRed" size={16} />
                <span>{t("hero.callDirect")}</span>
              </a>
            </div>
          </div>

          {/* Right Lottie Illustration Container */}
          <div className="lg:col-span-5 w-full max-w-[500px] mx-auto flex justify-center order-1 lg:order-2">
            <div className="relative w-full">
              <div className="absolute -inset-4 bg-gradient-to-tr from-brandRed/10 to-red-500/5 rounded-3xl filter blur-2xl -z-10" />
              <div className="w-full rounded-3xl bg-white/70 backdrop-blur-sm border border-slate-100 p-6 shadow-premium">
                <LottieAnimator lottie={lottie} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
